import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { forkJoin, map, Observable } from 'rxjs';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { locationInterface2 } from 'src/app/util/interfaces/locationInterface';
import { ciTypesInterface } from 'src/app/util/interfaces/ciTypes';

interface selectedValuesFilters {
	filterDate: 'today' | 'week' | 'month' | 'custom';
	location: string;
	ciType: string;
	subType: string;
	showInactiveCis: boolean;
	customDate: string;
	ciTypesArray?: Array<{ ciType: number; subType: string }>;
}

@Injectable({
	providedIn: 'root',
})
export class CmdbDashboardService {
	private getListKpiUrl = environment.baseApiUrl + 'myasset/dashboard';
	private getChartDataUrl = environment.baseApiUrl + 'myasset/flow/timeline';
	private getAvailableCiTypesUrl = environment.baseApiUrl + 'myasset/ciFilter';
	private getAllLocalitysURL = environment.baseApiUrl + 'location/search';
	private getCmdbExportUrl = environment.baseApiUrl + 'cmdbhp/export?general=&sortBy=id&sortDown=false&showActiveOnlyCis=true';
	private exportMovementsUrl = environment.baseApiUrl + 'myasset/flow/assets';

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService
	) {}

	getListKpi(selectValues: selectedValuesFilters): Observable<any> {
		const requestVariants = this.createCiSelectionVariants(selectValues);

		if (requestVariants.length === 1) {
			return this.http.get<any>(this.buildDashboardUrl(this.getListKpiUrl, requestVariants[0]), { headers: this.headers });
		}

		return forkJoin(
			requestVariants.map((requestVariant) =>
				this.http.get<any>(this.buildDashboardUrl(this.getListKpiUrl, requestVariant), { headers: this.headers })
			)
		).pipe(map((responses) => this.mergeDashboardResponses(responses)));
	}

	getDataChart(selectValues: selectedValuesFilters): Observable<any> {
		const requestVariants = this.createCiSelectionVariants(selectValues);

		if (requestVariants.length === 1) {
			return this.http.get<any>(this.buildDashboardUrl(this.getChartDataUrl, requestVariants[0]), { headers: this.headers });
		}

		return forkJoin(
			requestVariants.map((requestVariant) =>
				this.http.get<any>(this.buildDashboardUrl(this.getChartDataUrl, requestVariant), { headers: this.headers })
			)
		).pipe(map((responses) => this.mergeChartResponses(responses)));
	}

	getAvailablesCiTypes(): Observable<{ ciType: number; subType: number; type: string }[]> {
		return this.http.get<any>(this.getAvailableCiTypesUrl, { headers: this.headers });
	}

	getAvailablesLocations(): Observable<locationInterface2[]> {
		const defaultCustomer = this.ProfileService.userData().idCustomer;
		const getAllLocalitysURL = `${this.getAllLocalitysURL}?customer=${defaultCustomer}`;

		return this.http.get<any[]>(getAllLocalitysURL, { headers: this.headers }).pipe(
			map((localityRes) => {
				const newLocalityArray = [];
				for (const resp of localityRes) {
					const newLocality = {
						id_location: resp.id,
						location: resp.location,
						id_site: resp.site,
						site: resp.name_site,
					};
					newLocalityArray.push(newLocality);
				}

				return newLocalityArray;
			})
		);
	}

	exportData(selectValues: selectedValuesFilters): Observable<any> {
		const requestVariants = this.createCiSelectionVariants(selectValues);

		if (requestVariants.length === 1) {
			return this.http.get<any>(this.buildExportUrl(requestVariants[0]), { headers: this.headers });
		}

		return forkJoin(
			requestVariants.map((requestVariant) => this.http.get<any>(this.buildExportUrl(requestVariant), { headers: this.headers }))
		).pipe(map((responses) => this.mergeArrayResponses(responses)));
	}

	exportMovements(selectValues: selectedValuesFilters): Observable<any> {
		const requestVariants = this.createCiSelectionVariants(selectValues);

		if (requestVariants.length === 1) {
			return this.http.get<any>(this.buildMovementsUrl(requestVariants[0]), { headers: this.headers });
		}

		return forkJoin(
			requestVariants.map((requestVariant) => this.http.get<any>(this.buildMovementsUrl(requestVariant), { headers: this.headers }))
		).pipe(map((responses) => this.mergeArrayResponses(responses)));
	}

	private buildDashboardUrl(baseUrl: string, selectValues: selectedValuesFilters): string {
		let url = `${baseUrl}?period_type=${selectValues.filterDate}`;

		if (selectValues.filterDate === 'custom' && selectValues.customDate) {
			url += `&custom_date=${encodeURIComponent(selectValues.customDate)}`;
		}
		if (selectValues.ciType) {
			url += `&ci_type=${encodeURIComponent(selectValues.ciType)}`;
		}
		if (selectValues.subType) {
			url += `&subType=${encodeURIComponent(selectValues.subType)}`;
		}
		if (selectValues.location) {
			url += `&location=${encodeURIComponent(selectValues.location)}`;
		}
		url += `&showActiveOnlyCis=${!selectValues.showInactiveCis}`;

		return url;
	}

	private buildExportUrl(selectValues: selectedValuesFilters): string {
		let url = this.getCmdbExportUrl;

		if (selectValues.ciType) {
			url += `&type=${encodeURIComponent(selectValues.ciType)}`;
		}
		if (selectValues.subType) {
			url += `&sub_type=${encodeURIComponent(selectValues.subType)}`;
		}
		if (selectValues.location) {
			url += `&location=${encodeURIComponent(selectValues.location)}`;
		}

		url = url.replace('showActiveOnlyCis=true', `showActiveOnlyCis=${!selectValues.showInactiveCis}`);

		return url;
	}

	private buildMovementsUrl(selectValues: selectedValuesFilters): string {
		let url = `${this.exportMovementsUrl}?period_type=${selectValues.filterDate}`;

		if (selectValues.filterDate === 'custom' && selectValues.customDate) {
			url += `&custom_date=${encodeURIComponent(selectValues.customDate)}`;
		}
		if (selectValues.ciType) {
			url += `&ci_type=${encodeURIComponent(selectValues.ciType)}`;
		}
		if (selectValues.subType) {
			url += `&subType=${encodeURIComponent(selectValues.subType)}`;
		}
		if (selectValues.location) {
			url += `&location=${encodeURIComponent(selectValues.location)}`;
		}
		url += `&showActiveOnlyCis=${!selectValues.showInactiveCis}`;

		return url;
	}

	private createCiSelectionVariants(selectValues: selectedValuesFilters): selectedValuesFilters[] {
		const normalizedSelections = this.normalizeCiSelections(selectValues.ciTypesArray);

		if (normalizedSelections.length === 0) {
			return [selectValues];
		}

		return normalizedSelections.map((selection) => ({
			...selectValues,
			ciType: String(selection.ciType),
			subType: selection.subType,
			ciTypesArray: [selection],
		}));
	}

	private normalizeCiSelections(ciTypesArray?: Array<{ ciType: number; subType: string }>): Array<{ ciType: number; subType: string }> {
		if (!Array.isArray(ciTypesArray) || ciTypesArray.length === 0) {
			return [];
		}

		const parentTypeSet = new Set(
			ciTypesArray
				.filter((item) => item.subType === '' || item.subType === null || item.subType === undefined)
				.map((item) => item.ciType)
		);

		return ciTypesArray.filter((item) => {
			if (item.subType === '' || item.subType === null || item.subType === undefined) {
				return true;
			}

			return !parentTypeSet.has(item.ciType);
		});
	}

	private mergeDashboardResponses(responses: any[]): any {
		const mergedResponse = { flow: {}, users: {}, inconsistencies: {} };

		for (const response of responses) {
			this.mergeDashboardSection(mergedResponse.flow, response?.flow);
			this.mergeDashboardSection(mergedResponse.users, response?.users);
			this.mergeDashboardSection(mergedResponse.inconsistencies, response?.inconsistencies);
		}

		return mergedResponse;
	}

	private mergeDashboardSection(targetSection: any, sourceSection: any): void {
		if (!sourceSection) {
			return;
		}

		Object.entries(sourceSection).forEach(([key, value]: [string, any]) => {
			if (!targetSection[key]) {
				targetSection[key] = { ...value };
				return;
			}

			targetSection[key].quantity = Number(targetSection[key].quantity || 0) + Number(value?.quantity || 0);
		});
	}

	private mergeChartResponses(responses: any[]): any {
		const mergedLabels: string[] = [];
		const datasetAccumulator = new Map<string, { label: string; backgroundColor: string; data: Map<string, number> }>();

		for (const response of responses) {
			(response?.labels || []).forEach((label: string) => {
				if (!mergedLabels.includes(label)) {
					mergedLabels.push(label);
				}
			});

			(response?.datasets || []).forEach((dataset: any, datasetIndex: number) => {
				const datasetKey = dataset.label || `dataset-${datasetIndex}`;
				if (!datasetAccumulator.has(datasetKey)) {
					datasetAccumulator.set(datasetKey, {
						label: dataset.label,
						backgroundColor: dataset.backgroundColor,
						data: new Map<string, number>(),
					});
				}

				const mergedDataset = datasetAccumulator.get(datasetKey);
				(response?.labels || []).forEach((label: string, labelIndex: number) => {
					const currentValue = mergedDataset.data.get(label) || 0;
					mergedDataset.data.set(label, currentValue + Number(dataset.data?.[labelIndex] || 0));
				});
			});
		}

		return {
			labels: mergedLabels,
			datasets: [...datasetAccumulator.values()].map((dataset) => ({
				label: dataset.label,
				backgroundColor: dataset.backgroundColor,
				data: mergedLabels.map((label) => dataset.data.get(label) || 0),
			})),
		};
	}

	private mergeArrayResponses(responses: any[]): any[] {
		const mergedItems = new Map<string, any>();

		for (const item of responses.flat()) {
			const mergeKey = item && typeof item === 'object' && 'id' in item ? String(item.id) : JSON.stringify(item);
			if (!mergedItems.has(mergeKey)) {
				mergedItems.set(mergeKey, item);
			}
		}

		return [...mergedItems.values()];
	}
}
